import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Wifi, WifiOff, CheckCircle2, Loader2, Clock } from 'lucide-react';
import { todayMx } from '../lib/dateUtils';

const TZ = 'America/Tijuana';

function useMxClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

function formatMxTime(d) {
  return new Intl.DateTimeFormat('es-MX', { timeZone: TZ, hour: 'numeric', minute: '2-digit', hour12: true }).format(d).toUpperCase();
}

function formatMxDate(d) {
  return new Intl.DateTimeFormat('es-MX', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long' }).format(d);
}

export default function Attendance() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const nowDate = useMxClock();

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/attendance/status`);
      setStatus(r.data);
    } catch (err) {
      toast.error('Error al cargar estado');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => {
      // Dejar de sondear una vez ya registrado (ahorra batería en celular)
      if (status?.already_registered) return;
      load();
    }, 20000);
    return () => clearInterval(t);
  }, [load, status?.already_registered]);

  const doCheckin = async () => {
    setChecking(true);
    try {
      const r = await axios.post(`${API}/attendance/checkin`);
      toast.success('¡Llegada registrada!');
      setStatus(prev => ({ ...prev, already_registered: true, record: r.data.record }));
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se pudo registrar');
      // Recargar status para reflejar situación real
      load();
    } finally { setChecking(false); }
  };

  const firstName = (user?.full_name || 'Trabajador').split(' ')[0];
  const timeStr = formatMxTime(nowDate);
  const dateStr = formatMxDate(nowDate);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const connected = !!status?.connected;
  const already = !!status?.already_registered;
  const record = status?.record;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-8 bg-gradient-to-b from-background to-muted/40" data-testid="attendance-page">
      <div className="w-full max-w-md space-y-6 text-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">ASISTENCIA</h1>
          <p className="text-muted-foreground text-sm mt-1">Hola, <strong>{firstName}</strong></p>
        </div>

        <div className="space-y-1">
          <p className="text-lg capitalize" data-testid="attendance-date">{dateStr}</p>
          <p className="text-5xl font-bold tabular-nums" data-testid="attendance-time">{timeStr}</p>
        </div>

        {already && record ? (
          <Card className="border-2 border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30" data-testid="attendance-registered-card">
            <CardContent className="pt-6 pb-6 space-y-2">
              <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto" />
              <p className="text-xl font-bold text-emerald-800 dark:text-emerald-200">ASISTENCIA REGISTRADA</p>
              <p className="text-sm text-emerald-800/80 dark:text-emerald-200/80">Hora de llegada:</p>
              <p className="text-3xl font-bold text-emerald-800 dark:text-emerald-200 tabular-nums">{record.hora_llegada}</p>
              <p className="text-xs text-muted-foreground pt-2">Ya puedes cerrar esta pantalla.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className={`border-2 ${connected ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30' : 'border-slate-300 bg-slate-50 dark:bg-slate-900/30'}`} data-testid="attendance-wifi-card">
              <CardContent className="py-3 flex items-center justify-center gap-2 text-sm font-medium">
                {connected ? (
                  <><Wifi className="h-5 w-5 text-emerald-600" /><span className="text-emerald-800 dark:text-emerald-200">Internet de oficina detectado</span></>
                ) : (
                  <><WifiOff className="h-5 w-5 text-slate-500" /><span className="text-slate-700 dark:text-slate-300">Conéctate al WiFi de la oficina para registrar tu llegada</span></>
                )}
              </CardContent>
            </Card>

            <Button
              disabled={!connected || checking}
              onClick={doCheckin}
              className="w-full h-20 text-xl font-bold rounded-2xl shadow-lg"
              data-testid="attendance-checkin-btn"
            >
              {checking ? <><Loader2 className="h-6 w-6 animate-spin mr-2" />Registrando...</> : <><Clock className="h-6 w-6 mr-2" />REGISTRAR MI LLEGADA</>}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
